// Rollbaserad ingång till Bas-processen: vem behöver vilka dokument.
import { PROCESS_STEPS, type ProcessStepDef } from "./constants";

export type ProjectRole =
  | "Byggherre"
  | "Projektör"
  | "Bas-P"
  | "Bas-U"
  | "Entreprenör"
  | "Projektledare"
  | "Uppdragsledare"
  | "Förvaltare";

/** Roller som bara får läsa och granska – aldrig redigera. */
export const READ_ONLY_ROLES: ProjectRole[] = [
  "Projektledare",
  "Uppdragsledare",
  "Förvaltare",
];

export const isReadOnlyRole = (role?: string | null): boolean =>
  !!role && (READ_ONLY_ROLES as string[]).includes(role);

export const PROJECT_ROLES: ProjectRole[] = [
  "Byggherre",
  "Projektör",
  "Bas-P",
  "Bas-U",
  "Entreprenör",
  "Projektledare",
  "Uppdragsledare",
  "Förvaltare",
];

export interface RoleViewDef {
  role: ProjectRole;
  /** Kort beskrivning av rollens ansvar. */
  summary: string;
  /** Dokument rollen äger och ansvarar för. */
  owns: string[];
  /** Dokument rollen behöver läsa eller bidra till, utan att äga dem. */
  contributes: string[];
  /** Funktioner som ännu inte är byggda för rollen. */
  planned: string[];
}

const ownedBy = (match: (owner: ProcessStepDef["owner"]) => boolean) =>
  PROCESS_STEPS.filter((s) => match(s.owner)).map((s) => s.docNumber);

export const ROLE_VIEWS: Record<ProjectRole, RoleViewDef> = {
  Byggherre: {
    role: "Byggherre",
    summary:
      "Har det yttersta arbetsmiljöansvaret. Utser Bas-P och Bas-U skriftligt, gör förhandsanmälan och tar emot dokumentationen inför förvaltning.",
    owns: ownedBy((o) => o === "Byggherre"),
    contributes: ["12", "22", "31", "34", "43", "44", "46"],
    planned: [
      "Samlad slutdokumentation som ett paket inför förvaltning",
      "Programnivå som samlar flera delprojekt under ett paraply",
    ],
  },
  Projektör: {
    role: "Projektör",
    summary:
      "Ska projektera bort risker och redovisa de risker som blir kvar till Bas-P, både för byggskedet och för bruksskedet.",
    owns: ["16"],
    contributes: ["11", "13", "02", "31", "25", "44"],
    planned: [
      "Egna svar på funktionskraven i Dok 02 i stället för att Bas-P gissar",
    ],
  },
  "Bas-P": {
    role: "Bas-P",
    summary:
      "Samordnar arbetsmiljön under planering och projektering. Upprättar arbetsmiljöplanen och underlaget som Bas-U tar över.",
    owns: ownedBy((o) => o === "Bas-P"),
    contributes: ["20", "23", "25", "05", "16", "44"],
    planned: [
      "TA-plan som eget dokument med giltighetstid och ansvarig",
      "Avbrottsplan för el, gas och media på projektnivå",
    ],
  },
  "Bas-U": {
    role: "Bas-U",
    summary:
      "Samordnar arbetsmiljön under utförandet. Följer upp på plats, håller skyddsronder och styr entreprenörernas arbetsberedningar.",
    owns: ownedBy((o) => o === "Bas-U"),
    contributes: ["20", "23", "25", "12", "15"],
    planned: [
      "Kvittensuppföljning per etapp: vilka entreprenörer har läst AMP",
      "Bevakning av kompetensbevis och besiktningar som håller på att gå ut",
    ],
  },
  Entreprenör: {
    role: "Entreprenör",
    summary:
      "Utför arbetet. Ska ta del av arbetsmiljöplanen och ordningsreglerna, lämna arbetsberedning och rapportera tillbud.",
    owns: [],
    contributes: ["15", "24", "28", "12", "44", "45"],
    planned: ["Tillstånd för heta arbeten med giltighetstid och brandvakt"],
  },
  Projektledare: {
    role: "Projektledare",
    summary:
      "Granskande roll. Ser hela processen och all dokumentation men kan inte ändra något – följer upp vad som är klart, pågående och försenat.",
    owns: [],
    contributes: PROCESS_STEPS.map((s) => s.docNumber),
    planned: [],
  },
  Uppdragsledare: {
    role: "Uppdragsledare",
    summary:
      "Granskande roll. Ser hela processen och all dokumentation men kan inte ändra något – används för uppföljning och kvalitetsgranskning av uppdraget.",
    owns: [],
    contributes: PROCESS_STEPS.map((s) => s.docNumber),
    planned: [],
  },
  "Förvaltare": {
    role: "Förvaltare",
    summary:
      "Tar emot dokumentationen när bygget är klart och ansvarar för arbetsmiljön i bruksskedet. Läser slutdokumentationen, kvarvarande risker och uppföljningen – ändrar inget i byggskedet.",
    owns: [],
    contributes: ["22", "31", "32", "33", "34", "12"],
    planned: [],
  },
};

/** Dokument som är gemensamma för Bas-P och Bas-U läggs på båda rollerna. */
const SHARED_OWNERS: Record<string, ProjectRole[]> = Object.fromEntries(
  PROCESS_STEPS.filter((s) => s.owner === "Bas-P/Bas-U").map((s) => [
    s.docNumber,
    ["Bas-P", "Bas-U"] as ProjectRole[],
  ])
);

export const getRoleSteps = (role: ProjectRole): ProcessStepDef[] => {
  const def = ROLE_VIEWS[role];
  const owned = new Set(def.owns);
  Object.entries(SHARED_OWNERS).forEach(([doc, roles]) => {
    if (roles.includes(role)) owned.add(doc);
  });
  return PROCESS_STEPS.filter((s) => owned.has(s.docNumber));
};

export const getRoleContributions = (role: ProjectRole): ProcessStepDef[] => {
  const owned = new Set(getRoleSteps(role).map((s) => s.docNumber));
  return PROCESS_STEPS.filter(
    (s) => ROLE_VIEWS[role].contributes.includes(s.docNumber) && !owned.has(s.docNumber)
  );
};

/**
 * Vilka flikar i Bas-processen som är relevanta för respektive roll.
 * Övriga flikar göms bakom "Visa allt i processen" – inget tas bort.
 */
export const ROLE_TABS: Record<ProjectRole, string[]> = {
  Byggherre: [
    "minroll",
    "scenarier",
    "program",
    "meddelanden",
    "oversikt",
    "forhandsanmalan",
    "upphandling",
    "etapper",
    "skeden",
    "overlamning",
    "slutdok",
    "bruksuppfoljning",
    "tillbud",
    "introduktion",
    "granskning",
    "startbesked",
    "kemikalier",
    "tidslinje",
    "dagslage",
    "platsnarvaro",
    "konstruktioner",
    "disciplin",
    "byggherrehantering",
    "delegeringsordning",
    "delegeringsbrev",
    "byggherrechecklista",
    "byggherrerapport",
    "uppgiftsfordelning",
  ],
  Projektör: [
    "minroll",
    "meddelanden",
    "projektorrutin",
    "projektorinlamning",
    "arbetsomrade",
    "bruksskede",
    "projektorleverans",
    "qalogg",
    "oversikt",
  ],
  "Bas-P": [
    "minroll",
    "samordnadrisk",
    "samladarbetsomrade",
    "protokollanalys",
    "scenarier",
    "program",
    "sprak",
    "meddelanden",
    "inkorg",
    "oversikt",
    "samrutin",
    "arbetsomrade",
    "projektorinlamning",
    "forhandsanmalan",
    "upphandling",
    "tidplan",
    "tredjeman",
    "etapper",
    "skeden",
    "farligtmaterial",
    "skyddsregler",
    "taplaner",
    "avstangningar",
    "skedesplan",
    "overlamning",
    "qalogg",
    "granskning",
    "kemikalier",
    "tidslinje",
    "dagslage",
    "konstruktioner",
    "skyddsverksamhet",
    "delegeringsordning",
    "delegeringsbrev",
  ],
  "Bas-U": [
    "minroll",
    "meddelanden",
    "program",
    "sprak",
    "oversikt",
    "utforande",
    "tidplan",
    "tredjeman",
    "etapper",
    "skeden",
    "entreprenorer",
    "farligtmaterial",
    "elsakerhet",
    "hetaarbeten",
    "taplaner",
    "avstangningar",
    "kvittenser",
    "introduktion",
    "bevakning",
    "skyddsrond",
    "besiktningar",
    "samordning",
    "tillbud",
    "overlamning",
    "slutdok",
    "qalogg",
    "granskning",
    "startbesked",
    "kemikalier",
    "tidslinje",
    "dagslage",
    "basurutin",
    "basuchecklista",
    "skyddsverksamhet",
    "platsnarvaro",
    "konstruktioner",
    "toolbox",
    "disciplin",
    "delegeringsordning",
    "delegeringsbrev",
  ],
  Entreprenör: [
    "minroll",
    "meddelanden",
    "entreprenorer",
    "besiktningar",
    "elsakerhet",
    "hetaarbeten",
    "tillbud",
    "skyddsregler",
    "introduktion",
    "kemikalier",
    "toolbox",
    "konstruktioner",
  ],
  "Förvaltare": [
    "minroll",
    "oversikt",
    "overlamning",
    "slutdok",
    "bruksskede",
    "bruksuppfoljning",
    "skedesplan",
    "kemikalier",
    "tidslinje",
  ],
  // Granskarroller ser allt – flikarna sätts nedan.
  Projektledare: [],
  Uppdragsledare: [],
};

/** Alla flikar som förekommer i processen, oavsett roll. */
const ALL_TABS = Array.from(
  new Set([
    ...Object.values(ROLE_TABS).flat(),
    ...PROCESS_STEPS.map((s) => s.appLink).filter(Boolean).map(String),
  ])
);

ROLE_TABS.Projektledare = ALL_TABS;
ROLE_TABS.Uppdragsledare = ALL_TABS;
